<template>
  <TheAppLayout />
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { onMounted, watch } from 'vue'
import { useBroadcastChannel } from '@vueuse/core'
import useAuthStore from '@/modules/Auth/store'
import { useUserPreferencesQuery } from '@/modules/Core/requests/queries'
import TheAppLayout from '@/modules/Core/layouts/TheAppLayout.vue'
import useLocale from '@/modules/Core/composables/useLocale'
import useUserPreferencesStore from '@/modules/Core/store/userPreferences'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import { useItinerary } from '@/modules/Search/composables'
import {
  useCurrency,
  useSentry,
  useUpdateServiceWorker,
} from '@/modules/Core/composables'
import { MAIN_CHANNEL } from '@/modules/Core/constants'
import type { SignupConfirmSuccessResponse } from '@/modules/Auth/types/api/response/auth'

useLocale()

const { registerServiceWorker } = useUpdateServiceWorker()

const { data: bcData } = useBroadcastChannel({ name: MAIN_CHANNEL })

onMounted(() => {
  if (!import.meta.env.DEV) {
    registerServiceWorker()
  }
})

useHead({
  title: 'BudgetFly',
})

const { setFeatureFlagId } = useSentry()

// FIXME: SHOULD BE UPDATED WHEN DYNAMIC FEATUR FLAG IMPLEMENTED
setFeatureFlagId('')

// ITINERARY
const itinerarySummaryStore = useItinerarySummary()
const { fetchItinerary } = useItinerary()
const { data: itierariesData, isFetching: itierariesIsLoading } =
  fetchItinerary()

watch(itierariesData, () => {
  if (!itierariesData.value?.data) {
    return
  }

  itinerarySummaryStore.setItineraries(itierariesData.value.data)
})

watch(
  itierariesIsLoading,
  (value) => {
    itinerarySummaryStore.itinerariesIsLoading = value
  },
  {
    immediate: true,
  }
)

// CURRENCY
useCurrency()

// USER PREFERENCES
const authStore = useAuthStore()

const { data: userPreferencesData, refetch } = useUserPreferencesQuery({
  enabled: false,
})

const userPreferencesStore = useUserPreferencesStore()

function fetchUserPreferencesIfUserIsLoggedIn() {
  if (!authStore.loggedIn) {
    return
  }

  refetch()
}

onMounted(fetchUserPreferencesIfUserIsLoggedIn)

watch(bcData, (data) => {
  try {
    const { action, payload } = data as {
      action: string
      payload: SignupConfirmSuccessResponse
    }
    if (action === 'logout') {
      location.reload()
    } else if (action === 'login') {
      authStore.setTokens(payload)
      authStore.setStep('loggedIn')
      refetch()
    }
  } catch (e) {
    console.warn(e)
  }
})

watch(userPreferencesData, () => {
  if (!userPreferencesData.value?.data) {
    return
  }

  userPreferencesStore.setNotificationPreferences(
    userPreferencesData.value?.data.notification
  )
})

// LANGUAGE
const { applyCurrentLanguage } = useLocale()
onMounted(applyCurrentLanguage)
</script>
